import React, { useRef, useState, useEffect } from 'react';
import { Grid, Icon } from '@material-ui/core';
import classnames from 'classnames';

import { uuid } from '@klp/helpers';

import { FileObject } from '../classes';
import { useFileContext } from '../context/FileContext';

interface IProps {
    id: string;
    mimeTypes: string;
    queueName: string;
}

export const FileUpload: React.FC<IProps> = ({ id, mimeTypes, queueName }) => {
    const dropRef = useRef<HTMLLabelElement>(null);
    const [dragging, setDragging] = useState(false);
    const dragCounterRef = useRef(0);
    const { addToUploadQueue } = useFileContext();

    const handleDispatchUploadQueueAdd = (file: File) => {
        const fileObject = new FileObject({
            queueName,
            id: uuid(),
            fileName: file.name,
            fileContent: '',
            local: true,
            type: file.type,
        });
        const fileReaderData = new FileReader();
        fileReaderData.onload = async (ev: any) => {
            fileObject.fileContent = ev.target.result;
            await addToUploadQueue(new FileObject(fileObject));
        };
        fileReaderData.readAsDataURL(file);
    };

    const handleDrag = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragIn = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current += 1;
        if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
            setDragging(true);
        }
    };

    const handleDragOut = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounterRef.current -= 1;
        if (dragCounterRef.current <= 0) {
            setDragging(false);
        }
    };

    const handleDrop = (e: any) => {
        e.preventDefault();
        e.stopPropagation();

        const { files } = e.dataTransfer;
        if (files.length) {
            const filterAcceptList = Array.from<File>(files).filter(
                (file: File) => mimeTypes.includes(file.type),
            );
            filterAcceptList.forEach((file: File) =>
                handleDispatchUploadQueueAdd(file),
            );
        }

        e.dataTransfer.clearData();
        setDragging(false);
        dragCounterRef.current = 0;
    };

    const handleFileChange = (e: any) => {
        const { target } = e;
        Array.from<File>(target.files).forEach((file: File) =>
            handleDispatchUploadQueueAdd(file),
        );
    };

    useEffect(() => {
        const el = dropRef.current;
        if (!el) {
            return;
        }

        el.addEventListener('dragenter', handleDragIn);
        el.addEventListener('dragleave', handleDragOut);
        el.addEventListener('dragover', handleDrag);
        el.addEventListener('drop', handleDrop);

        return () => {
            el.removeEventListener('dragenter', handleDragIn);
            el.removeEventListener('dragleave', handleDragOut);
            el.removeEventListener('dragover', handleDrag);
            el.removeEventListener('drop', handleDrop);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const classNames = classnames('Gallery__ItemPicture--label', {
        'Gallery__ItemPicture--dragover': dragging,
    });

    let icon = 'add';
    if (dragging) {
        icon = 'cloud_upload';
    }

    return (
        <Grid item={true} xs={6} sm={4} md={3} className="Gallery__Item">
            <div className="Gallery__ItemPicture Gallery__ItemPicture--Loading">
                <input
                    accept={mimeTypes}
                    style={{ display: 'none' }}
                    id={id}
                    multiple={true}
                    type="file"
                    onChange={handleFileChange}
                />
                <label ref={dropRef} htmlFor={id} className={classNames}>
                    <Icon className="Gallery__ItemIcon">{icon}</Icon>
                </label>
            </div>
        </Grid>
    );
};
